import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/translate/naati — EN mirror
import { createFileRoute } from "@tanstack/react-router";
import { CertTypeHub } from "@/components/translate/CertTypeHub";
import { getCertType } from "@/data/translation-certtypes";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { naatiOfferCatalog, naatiServiceSchema, naatiSpeakableSchema, naatiPersonGraph } from "@/lib/naati-schema";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const cert = getCertType("naati")!;

export const Route = createFileRoute("/en/translate/naati/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("naati", "en", "https://nyclanguageinstitute.com/en/translate/naati") }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/en/translate/naati`;
    const title = `${cert.nameEn} | NYC Language Institute`;
    const desc = cert.taglineEn;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati", "en")},
        { name: "keywords", content: cert.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/translate/naati", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: `${SITE}/translate/naati` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/translate/naati` },
      ],
      scripts: [
        speakableSchema(),
        naatiSpeakableSchema(url),
        naatiOfferCatalog({ url, inLanguage: "en" }),
        naatiServiceSchema({ url, name: title, description: desc, inLanguage: "en" }),
        naatiPersonGraph(),
        howToSchema({
          name: "How to order a NAATI certified translation from NYC",
          description: cert.taglineEn,
          totalTime: "PT3D", estimatedCost: { currency: "THB", value: String(cert.priceFromThb) },
          steps: cert.stepsEn.map((s) => ({ name: s.name, text: s.text })),
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/en/translate/naati", serviceType: "NAATI Certified Translation", name: title, description: desc, priceFromThb: cert.priceFromThb }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`, inLanguage: "en",
              mainEntity: cert.faqsEn.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing NAATI certified translation tooling." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: () => <CertTypeHub cert={cert} locale="en" />,
});
